/**
 * One job, opened (#115).
 *
 * The page itself is `components/arrivals/Record`, which is also what an
 * arrival about no job is read on: one shape for both, so a Company with
 * a job page and a wrong number open in the same afternoon is not
 * reading two products. This route is the read that fills it.
 */
import { useEffect } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { ArrivalRecord } from "../components/arrivals/Record";
import { useThread } from "../lib/thread";

export default function JobPage() {
  const { ref = "" } = useParams<{ ref: string }>();
  // The list the reader came from, carried on the link they followed,
  // so Back returns to the calls they were reading rather than to an
  // unfiltered list. Absent when the URL was opened directly — from a
  // bookmark, or an older New Inquiry email.
  const state = useLocation().state as { from?: string } | null;
  const backTo = `/enquiries${state?.from ?? ""}`;
  const navigate = useNavigate();
  const thread = useThread({ job: ref }, () => navigate(backTo));

  useEffect(() => {
    document.title = "Enquiries | HireOS";
  }, []);

  return (
    <ArrivalRecord
      job={thread.job}
      loadError={thread.loadError}
      backTo={backTo}
      onEdited={thread.edited}
      onDelete={thread.removed}
      onSent={() => navigate(backTo)}
    />
  );
}

/**
 * The job page's old address, `/jobs/{ref}` (#130). It is written on
 * every job already drafted into a Company's rental system (#121) and
 * in every New Inquiry email already sent, so it goes to the page
 * rather than to Not Found. `replace`, so Back from the page goes to
 * wherever the reader came from rather than back into this redirect.
 */
export function OldJobPath() {
  const { ref = "" } = useParams<{ ref: string }>();
  return <Navigate to={`/enquiries/${encodeURIComponent(ref)}`} replace />;
}
